import { MIN_STAKE, type EvacuationMessage } from '@star-snake/shared'
import {
  NeonColors,
  neonButton,
  neonText,
  SCREEN_BASE_STYLE,
  fadeTransition,
  cosmicBackground,
  isTouchUiMode,
  FONTS,
} from '../neon-theme'
import { getPlayerColorKey, generateSnakeColors } from '../../game/snakeColors'
import { PaymentClient } from '../../net/PaymentClient'
import { NetworkManager } from '../../network/NetworkManager'
import { getStartActionState } from './startActionState'
import {
  drawResultSnake,
  drawStarBurst,
  drawStarGoal,
  formatSparkStarLine,
  getResultSnakeSpacing,
  getResultSnakeVisualSegments,
} from './resultSnakeArt'
import { getAppViewport, mountToAppShell } from '../appShell'

interface PraiseTier {
  minStars: number
  text: string
  color: string
  glowColor: string
}

const PRAISE_TIERS: PraiseTier[] = [
  { minStars: 11, text: 'LEGENDARY ESCAPE!', color: NeonColors.gold, glowColor: NeonColors.goldGlow },
  { minStars: 6, text: 'INCREDIBLE!', color: NeonColors.gold, glowColor: NeonColors.goldGlow },
  { minStars: 3, text: 'Great haul!', color: NeonColors.gold, glowColor: NeonColors.goldGlow },
  { minStars: 1, text: 'Nice escape!', color: NeonColors.textBright, glowColor: 'rgba(255,255,255,0.3)' },
  { minStars: 0, text: 'You made it out.', color: NeonColors.textMuted, glowColor: 'transparent' },
]

const MAX_CANVAS_DPR = 2

function setupHiDpiCanvas(canvas: HTMLCanvasElement, width: number, height: number): CanvasRenderingContext2D | null {
  const dpr = Math.min(window.devicePixelRatio || 1, isTouchUiMode() ? 1 : MAX_CANVAS_DPR)
  canvas.width = Math.floor(width * dpr)
  canvas.height = Math.floor(height * dpr)
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  return ctx
}

function getPraiseTier(starsEarned: number): PraiseTier {
  for (const tier of PRAISE_TIERS) {
    if (starsEarned >= tier.minStars) return tier
  }
  return PRAISE_TIERS[PRAISE_TIERS.length - 1]
}

export class EvacuationScreen {
  private container: HTMLDivElement
  private onPlayAgain: (() => void) | null = null
  private onMenu: (() => void) | null = null
  private animationFrameId: number | null = null
  private hideTimerId: number | null = null
  private revealTimers: number[] = []

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'evacuation-screen'
    this.container.style.cssText = `
      ${SCREEN_BASE_STYLE}
      background: ${NeonColors.bgDark};
      padding: 20px; box-sizing: border-box;
      opacity: 0;
      ${fadeTransition(300)}
    `
  }

  show(
    data: EvacuationMessage,
    onPlayAgain: () => void,
    onMenu: () => void,
    _entityId?: number,
    options?: { balance?: number; freeGamesLeft?: number },
  ): void {
    this.resetAnimations()
    this.onPlayAgain = onPlayAgain
    this.onMenu = onMenu
    this.container.innerHTML = ''
    const lightweightUi = isTouchUiMode()

    const balance = options?.balance ?? 0
    const freeGamesLeft = options?.freeGamesLeft ?? 0
    const startAction = getStartActionState(balance, freeGamesLeft)

    if (!lightweightUi) {
      this.container.insertAdjacentHTML('afterbegin', cosmicBackground())
    }

    const styleEl = document.createElement('style')
    styleEl.textContent = `
      @keyframes evacFadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes evacPulse {
        0%, 100% { text-shadow: 0 0 14px ${NeonColors.successGlow}, 0 0 34px ${NeonColors.successGlow}; }
        50% { text-shadow: 0 0 26px ${NeonColors.successGlow}, 0 0 60px ${NeonColors.successGlow}; }
      }
      .evac-fade-in {
        animation: evacFadeIn 0.45s ease-out forwards;
        opacity: 0;
      }
    `
    this.container.appendChild(styleEl)

    const content = document.createElement('div')
    content.style.cssText = 'z-index: 30; display: flex; flex-direction: column; align-items: center;'

    const title = document.createElement('h1')
    title.style.cssText = `
      font-size: clamp(2.4rem, 9vw, 3rem);
      font-weight: 900;
      letter-spacing: 0.18em;
      margin: 0 0 0.5rem 0;
      ${neonText(NeonColors.success, NeonColors.successGlow)}
      ${lightweightUi ? '' : 'animation: evacPulse 1.5s ease-in-out infinite;'}
      font-family: ${FONTS.title};
    `
    title.textContent = 'EVACUATED'
    content.appendChild(title)

    const subtitle = document.createElement('p')
    subtitle.style.cssText = `
      color: ${NeonColors.textMuted};
      margin: 0 0 1.4rem 0;
      font-size: 1rem;
      text-align: center;
      max-width: 320px;
    `
    subtitle.textContent = 'You escaped through the portal'
    content.appendChild(subtitle)

    const canvasWidth = Math.min(360, getAppViewport().width - 36)
    const canvasHeight = 132
    const snakeLength = Math.max(10, Math.floor(data.snakeLength || data.sparksCollected))
    const visualSegments = getResultSnakeVisualSegments(snakeLength, canvasWidth)
    const animationDurationMs = Math.min(4200, Math.max(1900, visualSegments * 48))
    const animCanvas = document.createElement('canvas')
    const ctx = setupHiDpiCanvas(animCanvas, canvasWidth, canvasHeight)
    animCanvas.style.cssText = 'display: block; margin: 0 auto 14px auto;'
    content.appendChild(animCanvas)

    const summaryLine = document.createElement('div')
    summaryLine.style.cssText = `
      min-height: 44px;
      font-size: 1rem;
      font-weight: 700;
      text-align: center;
      margin-bottom: 12px;
      ${neonText(NeonColors.gold, NeonColors.goldGlow)}
    `
    summaryLine.textContent = formatSparkStarLine(0, 'spark evacuated', 'sparks evacuated')
    content.appendChild(summaryLine)

    const praiseEl = document.createElement('div')
    praiseEl.style.cssText = 'min-height: 34px; margin-bottom: 22px; text-align: center; opacity: 0;'
    content.appendChild(praiseEl)

    const buttonsArea = document.createElement('div')
    buttonsArea.style.cssText = 'display: flex; gap: 12px; opacity: 0; pointer-events: none; flex-wrap: wrap; justify-content: center;'

    const playAgainBtn = document.createElement('button')
    playAgainBtn.style.cssText = `${neonButton(NeonColors.primary, NeonColors.primaryGlow)} font-size: 1.05rem;`
    playAgainBtn.innerHTML = `
      <span style="display: flex; align-items: center; gap: 12px;">
        <span>${startAction.kind === 'deposit' ? 'DEPOSIT START' : 'PLAY AGAIN'}</span>
        <span style="background: rgba(255,255,255,0.22); color: #0f172a; border-radius: 999px; padding: 4px 10px; font-size: 0.78rem; font-weight: 900; letter-spacing: 0.06em;">
          ${startAction.badgeText}
        </span>
      </span>
    `
    playAgainBtn.addEventListener('mouseenter', () => {
      playAgainBtn.style.transform = 'scale(1.03)'
      playAgainBtn.style.boxShadow = `0 0 40px ${NeonColors.primaryGlow}, 0 0 60px ${NeonColors.primaryGlow}`
    })
    playAgainBtn.addEventListener('mouseleave', () => {
      playAgainBtn.style.transform = 'scale(1)'
      playAgainBtn.style.boxShadow = `0 0 20px ${NeonColors.primaryGlow}, 0 0 40px ${NeonColors.primaryGlow}`
    })
    playAgainBtn.addEventListener('click', async () => {
      if (startAction.kind !== 'deposit') {
        this.onPlayAgain?.()
        this.hide()
        return
      }

      playAgainBtn.disabled = true
      playAgainBtn.style.opacity = '0.75'
      try {
        const network = NetworkManager.getInstance()
        const token = network.getToken()
        if (!token) throw new Error('Not authenticated')
        const paid = await PaymentClient.deposit(MIN_STAKE, token)
        await network.fetchBalance()
        if (paid) {
          this.show(data, onPlayAgain, onMenu, undefined, {
            balance: network.balance,
            freeGamesLeft: network.freeGamesLeft,
          })
          return
        }
      } catch {
        // Keep the screen visible and let the player retry.
      }

      playAgainBtn.disabled = false
      playAgainBtn.style.opacity = '1'
    })
    buttonsArea.appendChild(playAgainBtn)

    const menuBtn = document.createElement('button')
    menuBtn.style.cssText = `
      padding: 14px 24px; font-size: 1rem;
      background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
      border-radius: 16px; color: ${NeonColors.textMuted}; cursor: pointer;
      transition: color 0.15s, border-color 0.15s, background 0.15s;
      font-family: ${FONTS.body};
    `
    menuBtn.textContent = 'MENU'
    menuBtn.addEventListener('mouseenter', () => {
      menuBtn.style.color = NeonColors.textBright
      menuBtn.style.borderColor = 'rgba(255,255,255,0.2)'
      menuBtn.style.background = 'rgba(255,255,255,0.1)'
    })
    menuBtn.addEventListener('mouseleave', () => {
      menuBtn.style.color = NeonColors.textMuted
      menuBtn.style.borderColor = 'rgba(255,255,255,0.1)'
      menuBtn.style.background = 'rgba(255,255,255,0.05)'
    })
    menuBtn.addEventListener('click', () => {
      this.onMenu?.()
      this.hide()
    })
    buttonsArea.appendChild(menuBtn)
    content.appendChild(buttonsArea)

    if (startAction.helperText) {
      const helperEl = document.createElement('div')
      helperEl.style.cssText = `margin-top: 12px; font-size: 0.85rem; color: ${startAction.kind === 'trial' ? NeonColors.info : NeonColors.textMuted}; text-align: center;`
      helperEl.textContent = startAction.helperText
      content.appendChild(helperEl)
    }

    this.container.appendChild(content)
    mountToAppShell(this.container)
    requestAnimationFrame(() => { this.container.style.opacity = '1' })

    if (ctx) {
      this.startEvacAnimation(ctx, canvasWidth, canvasHeight, data.sparksCollected, data.starsEarned, snakeLength, summaryLine, praiseEl)
    } else {
      this.revealFinalState(summaryLine, praiseEl, data.sparksCollected, data.starsEarned)
    }

    const revealButtons = (): void => {
      buttonsArea.style.opacity = '1'
      buttonsArea.className = 'evac-fade-in'
      buttonsArea.style.pointerEvents = 'auto'
    }
    const revealId = window.setTimeout(revealButtons, animationDurationMs + 280)
    this.revealTimers.push(revealId)
  }

  hide(): void {
    this.resetAnimations()
    for (const el of this.container.querySelectorAll('.cosmic-nebula, .cosmic-star-field')) {
      (el as HTMLElement).style.display = 'none'
    }
    this.container.style.opacity = '0'
    this.hideTimerId = window.setTimeout(() => {
      this.container.remove()
      this.onPlayAgain = null
      this.onMenu = null
      this.hideTimerId = null
    }, 300)
  }

  dispose(): void {
    this.resetAnimations()
    this.container.remove()
    this.onPlayAgain = null
    this.onMenu = null
  }

  private startEvacAnimation(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    sparksCollected: number,
    starsEarned: number,
    snakeLength: number,
    summaryLine: HTMLDivElement,
    praiseEl: HTMLDivElement,
  ): void {
    const lightweightUi = isTouchUiMode()
    const frameStepMs = lightweightUi ? 33 : 0
    const colorSeed = parseInt(localStorage.getItem('star-snake-color-seed') || '0', 10) || 0
    const colors = generateSnakeColors(getPlayerColorKey(colorSeed))
    const starSize = 68
    const starInset = Math.max(94, starSize * 1.35)
    const starX = width - starInset
    const centerY = height * 0.5
    const visualSegments = getResultSnakeVisualSegments(snakeLength, width)
    const spacing = getResultSnakeSpacing(width, visualSegments)
    const catchX = starX - 18
    const startHeadX = -64
    const endHeadX = catchX + spacing * (visualSegments + 1)
    const startTime = performance.now()
    const totalDuration = Math.min(4200, Math.max(1900, visualSegments * 48))
    let displayedSparks = -1
    let displayedSegments = -1
    let lastStars = 0
    let lastDrawAt = 0
    const bursts: Array<{ startedAt: number; intensity: number }> = []
    const hitTimes: number[] = []

    const tick = (now: number): void => {
      if (frameStepMs > 0 && lastDrawAt > 0 && now - lastDrawAt < frameStepMs) {
        this.animationFrameId = requestAnimationFrame(tick)
        return
      }
      lastDrawAt = now
      const elapsed = now - startTime
      const progress = Math.min(1, elapsed / totalDuration)
      const eased = 1 - Math.pow(1 - progress, 2.45)
      const headX = startHeadX + (endHeadX - startHeadX) * eased
      const consumedSegments = Math.max(
        0,
        Math.min(visualSegments, Math.floor((headX - catchX) / spacing) + 1),
      )
      const currentSparks = Math.round(sparksCollected * (consumedSegments / visualSegments))
      const currentStars = Math.min(starsEarned, Math.floor(currentSparks / 100))

      if (consumedSegments > displayedSegments) {
        for (let seg = displayedSegments + 1; seg <= consumedSegments; seg++) {
          if (seg > 0) hitTimes.push(now)
        }
        displayedSegments = consumedSegments
      }
      for (let i = hitTimes.length - 1; i >= 0; i--) {
        if (now - hitTimes[i] > 200) hitTimes.splice(i, 1)
      }
      const goalPulse = Math.min(1.45, hitTimes.reduce((sum, hitTime) => sum + Math.max(0, 1 - (now - hitTime) / 200), 0))

      ctx.clearRect(0, 0, width, height)
      drawStarGoal(ctx, starX, centerY, starSize, Math.min(1.45, goalPulse + currentStars * 0.08))
      drawResultSnake(ctx, {
        canvasWidth: width,
        canvasHeight: height,
        headX,
        centerY,
        length: snakeLength,
        timeMs: now,
        colors,
        fadeToWhite: Math.max(0, (headX - (catchX - 34)) / 74),
        alpha: 1,
        consumedSegments,
      })

      for (const burst of bursts) {
        drawStarBurst(ctx, starX, centerY, burst.intensity, now - burst.startedAt)
      }
      for (let i = bursts.length - 1; i >= 0; i--) {
        if (now - bursts[i].startedAt > 420) bursts.splice(i, 1)
      }

      if (currentSparks !== displayedSparks) {
        displayedSparks = currentSparks
        summaryLine.textContent = formatSparkStarLine(currentSparks, 'spark evacuated', 'sparks evacuated')
      }

      if (currentStars > lastStars) {
        for (let star = lastStars + 1; star <= currentStars; star++) {
          bursts.push({ startedAt: now, intensity: Math.min(1.4, 0.42 + star * 0.1) })
          this.triggerHaptic(star)
        }
        lastStars = currentStars
      }

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(tick)
      } else {
        summaryLine.textContent = formatSparkStarLine(sparksCollected, 'spark evacuated', 'sparks evacuated')
        this.animationFrameId = null
        this.revealFinalState(summaryLine, praiseEl, sparksCollected, starsEarned)
      }
    }

    this.animationFrameId = requestAnimationFrame(tick)
  }

  private revealFinalState(
    summaryLine: HTMLDivElement,
    praiseEl: HTMLDivElement,
    sparksCollected: number,
    starsEarned: number,
  ): void {
    summaryLine.textContent = formatSparkStarLine(sparksCollected, 'spark evacuated', 'sparks evacuated')
    const tier = getPraiseTier(starsEarned)
    praiseEl.style.cssText = `
      min-height: 34px;
      margin-bottom: 22px;
      text-align: center;
      opacity: 1;
      font-size: ${starsEarned >= 6 ? '1.4rem' : '1.1rem'};
      font-weight: 800;
      ${neonText(tier.color, tier.glowColor)}
    `
    praiseEl.className = 'evac-fade-in'
    praiseEl.textContent = tier.text
  }

  private triggerHaptic(starNumber: number): void {
    const hf = window.Telegram?.WebApp?.HapticFeedback
    if (hf) {
      if (starNumber <= 2) hf.impactOccurred('light')
      else if (starNumber <= 5) hf.impactOccurred('medium')
      else hf.impactOccurred('heavy')
      return
    }
    if (navigator.vibrate) {
      navigator.vibrate(Math.min(120, 20 + starNumber * 12))
    }
  }

  private resetAnimations(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
    if (this.hideTimerId !== null) {
      clearTimeout(this.hideTimerId)
      this.hideTimerId = null
    }
    for (const timer of this.revealTimers) clearTimeout(timer)
    this.revealTimers = []
  }
}
